import React, {
  VoidFunctionComponent,
  ComponentProps,
  useMemo,
  useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import {
  VerticalTimeLine,
  VerticalTimeLineEntry
} from 'record-it-components/dateTime/VerticalTimeLine/VerticalTimeLine';
import { WorkTimeEntryBreak } from 'record-it-time-common/entity/entities/WorkTimeEntry/WorkTimeEntryDto';
import styles from './TrackedWorkTimeLine.module.css';

export type TrackedWorkTimeLineProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  startDate: string;
  endDate: string;
  workTimeEntryBreaks: Array<WorkTimeEntryBreak>;
};

export const TrackedWorkTimeLine: VoidFunctionComponent<TrackedWorkTimeLineProps> =
  React.memo((props) => {
    const { workTimeEntryBreaks, startDate, endDate, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();

    const sortedBreaks = useMemo<Array<WorkTimeEntryBreak>>(() => {
      return [...workTimeEntryBreaks].sort((a, b) => {
        return a.startDate.localeCompare(b.startDate);
      });
    }, [workTimeEntryBreaks]);

    const renderEntryFactory = useCallback(
      (type: 'break' | 'workTime'): (() => JSX.Element) => {
        // eslint-disable-next-line react/no-multi-comp
        return () => (
          <div
            className={styles.entry}
            data-entry-type={type}
          ></div>
        );
      },
      []
    );

    const breakEntriesWithFillers = useMemo<
      Array<VerticalTimeLineEntry>
    >(() => {
      const breakEntries = sortedBreaks.map<VerticalTimeLineEntry>(
        (workBreak) => ({
          key: workBreak.index,
          startDate: new Date(workBreak.startDate),
          endDate: new Date(workBreak.endDate),
          renderContent: renderEntryFactory('break')
        })
      );

      const entries: Array<VerticalTimeLineEntry> = [];

      for (const breakEntry of breakEntries) {
        const lastEntry = entries[entries.length - 1];
        if (
          lastEntry &&
          lastEntry.endDate.getTime() < breakEntry.startDate.getTime()
        ) {
          entries.push({
            key: `${lastEntry.key}-${breakEntry.key}`,
            startDate: new Date(lastEntry.endDate),
            endDate: new Date(breakEntry.startDate),
            renderContent: renderEntryFactory('workTime')
          });
        }

        entries.push(breakEntry);
      }

      return entries;
    }, [sortedBreaks, renderEntryFactory]);

    const entries = useMemo<Array<VerticalTimeLineEntry>>(() => {
      const firstBreakEntry = breakEntriesWithFillers[0];
      const lastBreakEntry =
        breakEntriesWithFillers[breakEntriesWithFillers.length - 1];

      if (firstBreakEntry && lastBreakEntry) {
        return [
          {
            key: 'startDateToFirstBreakFiller',
            startDate: new Date(startDate),
            endDate: new Date(firstBreakEntry.startDate),
            renderContent: renderEntryFactory('workTime')
          },
          ...breakEntriesWithFillers,
          {
            key: 'lastBreakToEndDateFiller',
            startDate: new Date(lastBreakEntry.endDate),
            endDate: new Date(endDate),
            renderContent: renderEntryFactory('workTime')
          }
        ];
      }

      return [
        {
          key: 'noBreaksFallback',
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          renderContent: renderEntryFactory('workTime')
        }
      ];
    }, [startDate, endDate, breakEntriesWithFillers, renderEntryFactory]);

    return (
      <div {...remainingProps}>
        <div className={styles.legend}>
          <span
            className={styles.legendColorPreview}
            data-entry-type="workTime"
          ></span>
          <span className={styles.legendColorText}>
            {t('components.timeTracking.TrackedWorkTimeLine.legend.workTime')}
          </span>
          <span
            className={styles.legendColorPreview}
            data-entry-type="break"
          ></span>
          <span className={styles.legendColorText}>
            {t('components.timeTracking.TrackedWorkTimeLine.legend.break')}
          </span>
        </div>
        <VerticalTimeLine
          entries={entries}
          data-testid="TrackedWorkTimeLine.VerticalTimeLine"
        ></VerticalTimeLine>
      </div>
    );
  });
